import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { resetPassword } from '../services/auth'
import { Truck, Eye, EyeSlash, CircleNotch, CheckCircle } from '@phosphor-icons/react'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!token || !email) {
      setError('Link inválido. Solicite um novo link de recuperação.')
      return
    }

    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem')
      return
    }

    setLoading(true)

    try {
      await resetPassword(email, token, newPassword)
      setSuccess(true)
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao redefinir senha. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  if (!token || !email) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center" style={{ background: 'rgb(37, 48, 55)' }}>
        <div className="flex items-center gap-3 mb-8">
          <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
            <Truck size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Eximtech</h1>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/40 max-w-md w-full mx-4 text-center">
          <h2 className="text-xl font-semibold text-white mb-4">Link inválido</h2>
          <p className="text-slate-400 mb-6">Este link de recuperação é inválido ou expirou.</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full text-white font-medium py-2.5 px-4 rounded-lg transition-all hover:shadow-lg"
            style={{ backgroundColor: 'rgb(5, 150, 105)' }}
          >
            Voltar para o login
          </button>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center" style={{ background: 'rgb(37, 48, 55)' }}>
        <div className="flex items-center gap-3 mb-8">
          <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
            <Truck size={32} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Eximtech</h1>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/40 max-w-md w-full mx-4 text-center">
          <CheckCircle size={48} weight="bold" className="text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Senha redefinida!</h2>
          <p className="text-slate-400">Redirecionando para o login...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center" style={{ background: 'rgb(37, 48, 55)' }}>
      {/* Logo */}
      <div className="flex items-center gap-3 mb-8">
        <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
          <Truck size={32} weight="bold" className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white">Eximtech</h1>
      </div>

      {/* Card */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/40 max-w-md w-full mx-4">
        <h2 className="text-xl font-semibold text-white mb-2">Redefinir senha</h2>
        <p className="text-slate-400 mb-6">Digite sua nova senha abaixo.</p>

        {error && (
          <div className="p-4 mb-6 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-slate-300 mb-2">
              Nova senha
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2.5 text-sm rounded-lg bg-white text-slate-900 border-0 focus:outline-none focus:ring-2 transition-all pr-10"
                style={{ '--tw-ring-color': 'rgb(5, 150, 105)' } as any}
                placeholder="Mínimo 6 caracteres"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-2">
              Confirmar nova senha
            </label>
            <input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2.5 text-sm rounded-lg bg-white text-slate-900 border-0 focus:outline-none focus:ring-2 transition-all"
              style={{ '--tw-ring-color': 'rgb(5, 150, 105)' } as any}
              placeholder="Digite a senha novamente"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full text-white font-medium py-2.5 px-4 rounded-lg transition-all hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ backgroundColor: 'rgb(5, 150, 105)' }}
          >
            {loading ? (
              <>
                <CircleNotch size={20} className="animate-spin" />
                Redefinindo...
              </>
            ) : (
              'Redefinir senha'
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full text-slate-400 hover:text-white font-medium py-2 transition-all"
          >
            Voltar para o login
          </button>
        </form>
      </div>
    </div>
  )
}
